/**
 * @fileoverview Authentication and authorization helper functions.
 * Provides utilities for role-based access control, module visibility checks,
 * and user session management.
 */

import _ from "lodash";
import * as Sentry from "@sentry/browser";
import store from "@/store";
import LocalStorageManager from "@/localStorageManager";

/**
 * Checks if the current user has any of the permitted roles.
 *
 * @param {string[]} permittedRoles - Array of role names to check against
 * @param {boolean} checkForModuleSettings - Whether to check if access control module is enabled
 * @returns {boolean} True if user has at least one permitted role or access control is disabled
 *
 * @example
 * const canAccess = checkRoles(['admin', 'supervisor']);
 */
export function checkRoles(
  permittedRoles: string[],
  checkForModuleSettings: boolean = true
): boolean {
  if (checkForModuleSettings) {
    const isAccessControlModuleEnabled = _.get(store, "state.modules.access_control.enabled");
    if (!isAccessControlModuleEnabled) {
      // if access_control not enabled, default allow all
      return true;
    }
  }

  const app_metadata = store.state.profile.app_metadata;
  const hasRole = app_metadata && _.intersection(app_metadata.roles, permittedRoles).length > 0;
  return hasRole;
}

/**
 * Gets the list of roles that have access to a specific module.
 *
 * @param {string} module - The module identifier to check access for
 * @returns {string[]} Array of role names with access to the module
 *
 * @example
 * const roles = getPermittedRoles('broadcast');
 * // Returns: ['admin', 'manager']
 */
export function getPermittedRoles(module: string): string[] {
  const roles = store.state.modules["access_control"].roles;
  return _.chain(roles)
    .map((role) => {
      return role.access[module] ? role.name : null;
    })
    .compact()
    .value();
}

/**
 * Checks if a module is enabled in the store configuration.
 *
 * @param {string} moduleId - The module identifier to check
 * @returns {boolean} True if the module is enabled
 *
 * @example
 * if (showModule('livechat')) {
 *   // Show livechat UI
 * }
 */
export function showModule(moduleId: string): boolean {
  return _.get(store, `state.modules[${moduleId}].enabled`, false);
}

/**
 * Logs out the current user and clears their session.
 * Dispatches logout actions and removes user from Sentry scope.
 *
 * @returns {void}
 *
 * @example
 * logout();
 */
export function logout() {
  store.dispatch("LOG_ALL_SESSION_OUT");
  store.dispatch("LOGOUT").then((_) => {});
  Sentry.configureScope((scope) => scope.setUser(null));
}

/**
 * Determines if the user should be logged out based on profile storage.
 *
 * @returns {boolean} True if no saved profile exists in local storage
 *
 * @example
 * if (shouldLogOut()) {
 *   // Redirect to login
 * }
 */
export function shouldLogOut() {
  const hasSavedProfile = LocalStorageManager.getItem("profile");
  return !hasSavedProfile;
}
